import { useState, useEffect } from "react";
import { CheckCompany, CheckSignatory, isCheckError, CheckResponseError } from "winslow-core";
import { ModalComponentWrapper } from "../../shared/ModalComponentWrapper";
import { Alert, AlertDescription } from "../../../ds2/components";
import { CheckEmbeddedComponentV2 } from "../../payroll/CheckEmbeddedComponentV2";
import { apiCheckGetCompanyFilingComponent } from "../../../services/PayrollService";
import toast from "react-hot-toast";
import { toastFail } from "../../../config";

type ModalCheckCompanyFilingProps = {
    isOpen: boolean;
    checkCompany: CheckCompany | null;
    checkSignatories: CheckSignatory[] | null;
    onClose: () => void;
};

export const ModalCheckCompanyFiling = ({
    isOpen,
    checkCompany,
    checkSignatories,
    onClose,
}: ModalCheckCompanyFilingProps) => {
    const [componentUrl, setComponentUrl] = useState<string | null>(null);
    const [loadingComponent, setLoadingComponent] = useState<boolean>(false);
    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);

    // Load component URL when modal opens
    useEffect(() => {
        if (isOpen && checkCompany && checkSignatories && checkSignatories.length > 0) {
            loadComponentUrl();
        } else {
            // Reset state when modal closes
            setComponentUrl(null);
            setCheckResponseError(undefined);
        }
    }, [isOpen, checkCompany, checkSignatories]);

    const loadComponentUrl = async () => {
        if (!checkCompany?.id || !checkSignatories || checkSignatories.length === 0) {
            return;
        }

        const firstSignatory = checkSignatories[0];
        if (!firstSignatory?.id) {
            return;
        }

        setLoadingComponent(true);
        setCheckResponseError(undefined);

        try {
            const apiResponse = await apiCheckGetCompanyFilingComponent(checkCompany.id, firstSignatory.id);
            const url = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !url) {
                toast("Error loading filing component", toastFail);
                return;
            }

            setComponentUrl(url);
        } catch (error) {
            console.error("[ModalCheckCompanyFiling] Error loading component URL:", error);
            toast("Error loading filing component", toastFail);
        } finally {
            setLoadingComponent(false);
        }
    };

    return (
        <ModalComponentWrapper
            isOpen={isOpen}
            onClose={onClose}
            style={{ width: "90%", maxWidth: "1200px", maxHeight: "90vh", overflow: "auto" }}
        >
            <>
                {checkResponseError && (
                    <Alert variant="warning" style={{ marginBottom: "16px" }}>
                        <AlertDescription>
                            {checkResponseError.errorMessage || "An error occurred"}
                        </AlertDescription>
                    </Alert>
                )}

                {!checkSignatories || checkSignatories.length === 0 ? (
                    <Alert variant="warning">
                        <AlertDescription>
                            No signatories found. Please add a signatory before embedding the filing authorization component.
                        </AlertDescription>
                    </Alert>
                ) : loadingComponent ? (
                    <div style={{ padding: "40px", textAlign: "center" }}>
                        Loading filing component...
                    </div>
                ) : componentUrl ? (
                    <div className="app-pay-enroll-embed">
                        <CheckEmbeddedComponentV2
                            url={componentUrl}
                            title="Filing Authorization"
                            onEvent={(event, data) => {
                                console.log("[ModalCheckCompanyFiling] Check component event:", event, data);
                                // Handle events if needed
                            }}
                        />
                    </div>
                ) : (
                    <Alert variant="warning">
                        <AlertDescription>
                            Component not found.
                        </AlertDescription>
                    </Alert>
                )}
            </>
        </ModalComponentWrapper>
    );
};

